import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Banknote, CheckCircle2, Send, ImagePlus, X, Camera } from "lucide-react";

export default function SellCar() {
  const fileRef = useRef();
  const location = useLocation();
  const [form, setForm] = useState({
    brand: "",
    model: "",
    year: "",
    km: "",
    fuel: "",
    transmission: "",
    condition: "",
    requested_price: "",
    name: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [files, setFiles] = useState([]); // local File[]
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) {
      setForm((f) => ({
        ...f,
        brand: prefill.brand || f.brand,
        model: prefill.model || f.model,
        year: prefill.year || f.year,
        km: prefill.km || f.km,
      }));
      // scroll to top so user sees the prefilled form
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location.state]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    const remaining = 8 - files.length;
    if (arr.length > remaining) toast.warning(`Puoi caricare al massimo 8 foto.`);
    const toAdd = arr.slice(0, remaining);
    setFiles((prev) => [...prev, ...toAdd]);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model || !form.year || !form.km || !form.requested_price || !form.name || !form.phone || !form.email) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        year: parseInt(form.year),
        km: parseInt(form.km),
        requested_price: parseFloat(form.requested_price),
      };
      const r = await api.post("/sell-offers", payload);
      const offerId = r.data.id;
      // Upload photos sequentially so backend enforces the 8-cap correctly
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/sell-offers/${offerId}/images`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          toast.error(`Errore upload di ${f.name}`);
        }
      }
      setSent(true);
      toast.success("Proposta inviata! Rocco ti ricontatterà a breve.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio proposta");
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="App min-h-screen flex flex-col">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center flex-1" data-testid="sell-success">
          <CheckCircle2 size={60} className="text-gold mx-auto mb-6" />
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">Proposta inviata</h1>
          <p className="text-midnight/70 text-lg leading-relaxed mb-8 max-w-xl mx-auto">
            Grazie {form.name}. Rocco valuterà la tua auto e ti contatterà entro 48 ore per una proposta di acquisto.
          </p>
          <button
            onClick={() => {
              setSent(false);
              setForm({ brand: "", model: "", year: "", km: "", fuel: "", transmission: "", condition: "", requested_price: "", name: "", phone: "", email: "", notes: "" });
              setFiles([]);
            }}
            className="text-sm uppercase tracking-widest text-gold hover:underline"
            data-testid="new-sell-offer-btn"
          >
            Invia un'altra proposta
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App min-h-screen flex flex-col" data-testid="sell-page">
      <Header />

      <section className="max-w-6xl mx-auto px-5 md:px-10 py-16 md:py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="divider-gold" />
              <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">
                Vendi la tua Auto
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-6 leading-tight heading-gold-silver" data-testid="sell-hero-title">
              Noi compriamo <br /><span className="italic">la vostra auto</span>
            </h1>
            <p className="text-white/80 leading-relaxed mb-8">
              Guzzardella Auto acquista veicoli da privati con procedura semplice e trasparente. Compila il modulo, aggiungi le foto della tua auto, e riceverai una proposta seria in tempi rapidi.
            </p>
            <div className="card-sharp p-6 surface-silver text-midnight">
              <div className="flex items-center gap-3 mb-3">
                <Banknote size={18} className="text-gold" />
                <h3 className="font-display text-lg font-bold text-midnight">Come funziona</h3>
              </div>
              <ol className="space-y-3 text-sm text-midnight font-medium list-none">
                <li className="flex gap-3"><span className="text-gold font-bold">1.</span> Compila il modulo con i dati dell'auto</li>
                <li className="flex gap-3"><span className="text-gold font-bold">2.</span> Carica alcune foto (esterno + interno)</li>
                <li className="flex gap-3"><span className="text-gold font-bold">3.</span> Rocco ti contatta con una proposta</li>
                <li className="flex gap-3"><span className="text-gold font-bold">4.</span> Pagamento immediato al ritiro</li>
              </ol>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 card-sharp p-8 md:p-10 space-y-6" data-testid="sell-form">
            <div>
              <p className="text-xs uppercase tracking-widest text-gold mb-4">Dati dell'auto</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Marca *</Label>
                  <Input required value={form.brand} onChange={update("brand")} placeholder="Es. Audi" className="mt-2 rounded-sm" data-testid="sell-brand-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Modello *</Label>
                  <Input required value={form.model} onChange={update("model")} placeholder="Es. A4 Avant" className="mt-2 rounded-sm" data-testid="sell-model-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Anno *</Label>
                  <Input required type="number" value={form.year} onChange={update("year")} placeholder="2018" className="mt-2 rounded-sm" data-testid="sell-year-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Chilometri *</Label>
                  <Input required type="number" value={form.km} onChange={update("km")} placeholder="80000" className="mt-2 rounded-sm" data-testid="sell-km-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Alimentazione</Label>
                  <select
                    value={form.fuel}
                    onChange={update("fuel")}
                    className="mt-2 w-full rounded-sm px-3 h-10 text-sm select-silver"
                    data-testid="sell-fuel-input"
                  >
                    <option value="">-- Seleziona --</option>
                    <option value="Benzina">Benzina</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Ibrida">Ibrida</option>
                    <option value="Elettrica">Elettrica</option>
                    <option value="GPL">GPL</option>
                    <option value="Metano">Metano</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Cambio</Label>
                  <select
                    value={form.transmission}
                    onChange={update("transmission")}
                    className="mt-2 w-full rounded-sm px-3 h-10 text-sm select-silver"
                    data-testid="sell-transmission-input"
                  >
                    <option value="">-- Seleziona --</option>
                    <option value="Manuale">Manuale</option>
                    <option value="Automatico">Automatico</option>
                    <option value="Semi-automatico">Semi-automatico</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Stato dell'auto</Label>
                  <div className="mt-2 grid grid-cols-3 gap-2" data-testid="sell-condition-group">
                    {[
                      { v: "excellent", label: "Ottimo", desc: "come nuova" },
                      { v: "good", label: "Buono", desc: "segni d'uso" },
                      { v: "fair", label: "Da sistemare", desc: "richiede lavori" },
                    ].map((c) => (
                      <button
                        key={c.v}
                        type="button"
                        onClick={() => setForm({ ...form, condition: c.v })}
                        className={`px-3 py-2 text-xs uppercase tracking-widest rounded-sm border transition-colors ${
                          form.condition === c.v
                            ? "border-gold bg-gold/10 text-midnight font-semibold"
                            : "border-border text-midnight/60 hover:border-gold/50"
                        }`}
                        data-testid={`sell-condition-${c.v}`}
                      >
                        <span className="block">{c.label}</span>
                        <span className="block text-[9px] text-midnight/40 normal-case tracking-normal">{c.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Prezzo richiesto (€) *</Label>
                  <Input required type="number" step="100" value={form.requested_price} onChange={update("requested_price")} placeholder="15000" className="mt-2 rounded-sm" data-testid="sell-price-input" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-xs uppercase tracking-widest text-gold mb-4">I tuoi contatti</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Nome e Cognome *</Label>
                  <Input required value={form.name} onChange={update("name")} className="mt-2 rounded-sm" data-testid="sell-name-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Telefono *</Label>
                  <Input required type="tel" value={form.phone} onChange={update("phone")} className="mt-2 rounded-sm" data-testid="sell-phone-input" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Email *</Label>
                  <Input required type="email" value={form.email} onChange={update("email")} className="mt-2 rounded-sm" data-testid="sell-email-input" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-widest text-gold">Foto ({files.length}/8)</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                data-testid="sell-photos-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={files.length >= 8}
                className="w-full border-2 border-dashed border-border hover:border-gold hover:bg-gold/5 transition-colors p-6 text-center rounded-sm mb-4 disabled:opacity-50"
                data-testid="sell-add-photos-btn"
              >
                <Camera size={22} className="text-gold mx-auto mb-2" />
                <p className="text-sm text-midnight/70">
                  Clicca per aggiungere foto (esterno, interno, motore)
                </p>
                <p className="text-[10px] text-midnight/40 mt-1">JPG, PNG, WebP · Max 8 foto · 8MB ciascuna</p>
              </button>
              {files.length > 0 && (
                <div className="grid grid-cols-4 gap-2" data-testid="sell-photos-preview">
                  {files.map((f, i) => (
                    <div key={i} className="relative aspect-square border border-border overflow-hidden group">
                      <img src={URL.createObjectURL(f)} className="w-full h-full object-cover" alt="" />
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="absolute top-1 right-1 p-1 bg-destructive text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`sell-remove-photo-${i}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-border">
              <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Note aggiuntive (opzionale)</Label>
              <Textarea
                value={form.notes}
                onChange={update("notes")}
                rows={4}
                placeholder="Condizioni, storia, tagliandi, incidenti, optional..."
                className="mt-2 rounded-sm"
                data-testid="sell-notes-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full flex items-center justify-center gap-2 px-6 py-4 text-sm uppercase tracking-widest font-semibold disabled:opacity-50"
              data-testid="sell-submit-btn"
            >
              <Send size={16} />
              {loading ? "Invio..." : "Invia Proposta"}
            </button>
            <p className="text-xs text-midnight/40 text-center">
              I campi contrassegnati con * sono obbligatori
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
